/**
 * The one construction of the public confirmation-page path (plan
 * 2026-07-31-002, U3).
 *
 * The confirmation page is email-gated: it renders an order only when the URL
 * carries BOTH `ref` and an `email` that matches the order's buyer email
 * (BE-TICKETS-002 — a mismatch 404s exactly like a bad reference code). So the
 * two params are not optional decoration, they are the credential pair, and
 * every surface that links to that page has to build them the same way.
 *
 * Before this file the construction existed three times: nested un-exported
 * inside `rsvp-form-client.tsx` (post-checkout redirect + the Stripe return
 * URLs), and again in the backend's `send-confirmation-email.ts`. The my-tickets
 * page would have been a fourth. The backend copy stays where it is — it builds
 * an ABSOLUTE url against the server-side `WEB_APP_BASE_URL` and cannot import
 * from `apps/web` — but the two web surfaces now share this one.
 *
 * The return value is a root-relative path (safe for `router.push` and for
 * `next/link`), and it always ends inside the query string so callers can append
 * `&acct=...` / `&payment_intent_client_secret=...` to it, which the checkout
 * return path does.
 */

export interface ConfirmationPathParams {
  schoolSlug: string
  eventPublicId: string
  referenceCode: string
  /** MUST be the order's buyer email — the page's lookup is gated on matching it. */
  buyerEmail: string
  /**
   * Optional, and honestly so: it is a rendering hint, not part of the lookup.
   * The confirmation page reads it only to pick its headline copy, and falls
   * back to the kind on the event it fetches for itself when the param is
   * absent (`resolveConfirmationCopy` in `confirmation/page.tsx`). Checkout
   * passes it because it already holds the event; the my-tickets page omits it
   * because an order summary row carries no event kind, and the fallback covers
   * that with no visible difference.
   */
  kind?: string
}

export function buildConfirmationPath(params: ConfirmationPathParams): string {
  const query = new URLSearchParams({ ref: params.referenceCode, email: params.buyerEmail })
  if (params.kind) query.set('kind', params.kind)
  return (
    `/events/${encodeURIComponent(params.schoolSlug)}/${encodeURIComponent(params.eventPublicId)}` +
    `/confirmation?${query.toString()}`
  )
}
